import env from '~/libs/env';
import ZendeskChatService from "~/libs/zendesk-chat-service";
import IntercomChatService from "~/libs/intercom-chat-service"
import { useBorrowerStore } from '~/store/borrower';
import { useRootStore } from '~/store/root';
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization';
import { useAppAnalyticsStore } from '~/store/app-analytics';

/**
 * Injects $chatService instance into nuxt application.
 *
 * Currently, provides an instance of ZendeskChatService for Embedded and Intercom for non-Embedded.
 */
export default defineNuxtPlugin(async ({ $pinia, _route }) => {
  const nuxtApp = useNuxtApp()

  let chatService = {
    show() {},
    hide() {},
    open() {},
  };

  let chatEnabled = env('zendeskChat') === 'true' || env('zendeskChat') === true
  if (useAffiliateCustomizationStore($pinia).chatEnabled === false) {
    chatEnabled = false
  }

  if (chatEnabled) {
    const isEmbedded = _route.path.startsWith('/embedded')
    const intercom_hash = await useRootStore($pinia).getIntercomHash()
    let chatExperience = 'intercom'

    // TODO: Need embedded app configured and initialized for Intercom
    if (isEmbedded || !intercom_hash) {
      chatExperience = 'zendesk'
      chatService = new ZendeskChatService({
        key: getKey({
          isEmbedded
        })
      })
    } else {
      chatService = new IntercomChatService()
    }

    nuxtApp.hook('page:finish', () => {
      setChatMetaData($pinia, chatExperience);
    })
  }

  nuxtApp.provide('chatService', chatService)
})

function getKey({ isEmbedded }) {
  let key;
  if (isEmbedded) {
    key = env('zendeskChatKeyEmbedded');
    console.log('initializing embedded chat');
  } else {
    key = env('zendeskChatKeyMarketplace');
    console.log('initializing marketplace chat');
  }
  return key;
}

/**
* API Documentation: https://developer.zendesk.com/api-reference/widget-messaging/web/core/#set-conversation-fields
* Support Article: https://support.zendesk.com/hc/en-us/articles/5658339908378
* Sets custom ticket field values to provide more context for ticket agents
* */
const authorizeUser = (zendeskEndUserJwt) => {
  if (window.zE !== undefined) {
    zE('messenger', 'loginUser', (callback) => {
      callback(zendeskEndUserJwt)
    })
  }
}
const setConversationFields = (borrower) => {
  let retryCount = 0
  if(window.zE !== undefined){
    zE("messenger:set", "conversationFields", [
      // Zendesk ticket fields
      { id: "20620426449171", value: "" + borrower.id }, // Borrower ID Reference field
      { id: "25794213312659", value: borrower.mineralGroup },
      { id: "25794137624595", value: borrower.status }
    ]);
  } else {
    if(retryCount < 8){
      setTimeout(setConversationFields, 500, borrowerId)
      retryCount++
    } else {
      throw Error(`Zendesk is not available after ${retryCount} retries`)
    }
  }
}

const bootIntercom = async ($pinia) => {
  if (window.Intercom !== undefined) {
    const appAnalyticsStore = useAppAnalyticsStore($pinia)
    const user = useRootStore($pinia).authUser
    const user_id = appAnalyticsStore.getLendioId
    const intercom_hash = await useRootStore($pinia).getIntercomHash()
    const borrower = await useBorrowerStore($pinia).getBorrower()

    if (!user || !borrower) return

    /**
     * Developer Note:
     * Intercom('update', { key: val }) API method is alos available in the future to
     * easily update the user / borrower's values as they progress through an application.
     * https://developers.intercom.com/installing-intercom/web/methods/#intercomupdate
     */

    window.Intercom("boot", {
      api_base: "https://api-iam.intercom.io",
      app_id: env('intercomAppId'),
      user_hash: intercom_hash,
      user_id: user_id,
      name: `${user.first} ${user.last}`, // Full name
      email: user.email,
      utm_campaign: user.marketingData?.campaign || '',
      utm_content: user.marketingData?.content || '',
      utm_medium: user.marketingData?.medium || '',
      utm_source: user.marketingData?.source || '',
      utm_term: user.marketingData?.term || '',
      tenant_id: user.tenantId,
      logins: user.logins,
      company: {
        company_id: borrower.id,
        borrower_id: borrower.id, // let's explicitly set a borrowerId in intercom to denote the company_id = borrower_id
        name: borrower.name,
        industry: borrower.industryName,
        is_test: borrower.isTest,
        mineral_group: borrower.mineralGroup,
        stage: borrower.stage,
        status: borrower.status,
        phone: borrower.phone,
        timezone: borrower.timezone
      }
    })
  }
}

async function setChatMetaData($pinia, chatExperience = 'intercom') {
  if (chatExperience === 'intercom') {
    await bootIntercom($pinia)
  } else {
    const zendeskJwt = useRootStore($pinia).zendeskJwt
    if (zendeskJwt) {
      authorizeUser(zendeskJwt)
    }

    await useBorrowerStore($pinia).getBorrower().then((borrower) => {
      if (!borrower) return;
      // set ticket meta data (ticket custom fields)
      setConversationFields(borrower)
    });
  }
}
